import { inject, Injectable } from '@angular/core';

import { NewsItem } from '../../shared/interfaces';
import { SignageConfigurationModel } from '../config/models';
import { PresentationContentModel, RuntimeSlideModel, SlideTiming } from './models';
import { SlideDurationPolicyService } from './slide-duration-policy.service';
import { ContentKind, SlideLayout, SlideTimingKind } from './enums';

const VIDEO_SLIDE_MS = 90_000;

// News and birthdays share a shape, so `birthday` only swaps the layout and drops the category label.
@Injectable({ providedIn: 'root' })
export class NewsSlideBuilder {
  private readonly duration = inject(SlideDurationPolicyService);

  public build(items: NewsItem[], config: SignageConfigurationModel, birthday: boolean = false): RuntimeSlideModel[] {
    return items.flatMap(item => this.slidesFor(item, config, birthday));
  }

  private slidesFor(item: NewsItem, config: SignageConfigurationModel, birthday: boolean): RuntimeSlideModel[] {
    const kind = birthday ? ContentKind.Birthdays : ContentKind.News;
    const images = item.pictureCollection || [];
    const showVideo = !birthday && item.visibleContentTypeId === 2 && !!item.bodyContent?.videos?.length;
    const content = this.contentFor(item, config, birthday, images, showVideo);

    const fullscreen = !birthday && ((item.scaleImagesToFit && images.length > 0) || (item.scaleVideoToFit && showVideo));
    const expiry = item.publishedEndTime ? Date.parse(item.publishedEndTime) : undefined;
    const videoTiming: SlideTiming = { kind: SlideTimingKind.Media, expectedMilliseconds: VIDEO_SLIDE_MS };
    const textTiming = this.duration.timingFor(content.bodyText, config);

    const slide: RuntimeSlideModel = {
      id: `${kind}:${item.id}:detail`, kind, layout: birthday ? SlideLayout.Backdrop : SlideLayout.Split, content, images,
      // A detail slide whose video plays on the fullscreen slide shows only a thumbnail, so nothing would ever end
      // a media wait; without reading time it was skipped the moment it appeared.
      duration: showVideo && !fullscreen ? videoTiming : textTiming,
      carouselSecondsPerImage: item.imageCarrouselSpeed > 0 ? item.imageCarrouselSpeed : config.carouselSecondsPerImage,
      hasFullscreenContent: fullscreen,
      meta: { sourceId: item.id, ...(Number.isFinite(expiry) ? { expiresAt: expiry } : {}) }
    };

    if (!fullscreen) {
      return [slide];
    }

    // Media marked scale-to-fit gets a second, full-bleed slide after the readable one — text first, then the image.

    return [slide, {
      ...slide, id: `${kind}:${item.id}:fullscreen`, layout: SlideLayout.Fullscreen, hasFullscreenContent: false,
      duration: showVideo ? videoTiming : textTiming
    }];
  }

  private contentFor(item: NewsItem, config: SignageConfigurationModel, birthday: boolean,
                     images: string[], showVideo: boolean): PresentationContentModel {
    return {
      id: item.id, title: item.subject, bodyText: this.duration.shorten(item.bodyContent?.text || ''),
      label: birthday ? null : config.raw.serviceNames.find(service => service.serviceType === item.newsCategory)?.value,
      providerChannelIds: item.providerChannelIds || [], images, showVideo,
      video: showVideo ? item.bodyContent.videos[0] : null
    };
  }
}
